import { __extends } from "tslib";
import { ASObject } from '@awayfl/avm2';
import { clampS8U8, toS16 } from '@awayfl/swf-loader';
import { AbstractMethodError, ColorTransform as AwayColorTransform } from '@awayjs/core';
/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Class: ColorTransform
var ColorTransform = /** @class */ (function (_super) {
    __extends(ColorTransform, _super);
    function ColorTransform(redMultiplierAdaptee, greenMultiplier, blueMultiplier, alphaMultiplier, redOffset, greenOffset, blueOffset, alphaOffset) {
        if (redMultiplierAdaptee === void 0) { redMultiplierAdaptee = 1; }
        if (greenMultiplier === void 0) { greenMultiplier = 1; }
        if (blueMultiplier === void 0) { blueMultiplier = 1; }
        if (alphaMultiplier === void 0) { alphaMultiplier = 1; }
        if (redOffset === void 0) { redOffset = 0; }
        if (greenOffset === void 0) { greenOffset = 0; }
        if (blueOffset === void 0) { blueOffset = 0; }
        if (alphaOffset === void 0) { alphaOffset = 0; }
        var _this = _super.call(this) || this;
        if (redMultiplierAdaptee instanceof AwayColorTransform) {
            _this._adaptee = redMultiplierAdaptee;
        }
        else {
            // default args on constructor save ONLY when args not passed (undef)
            // but AVM can pass and non valid args, like NaN
            var rm = +redMultiplierAdaptee, gm = +greenMultiplier, bm = +blueMultiplier, am = +alphaMultiplier;
            _this._adaptee = new AwayColorTransform(
            // because default value is 1, need check strict (except 0)
            // NaN === NaN -> false, fast NaN isNaN check
            (rm === rm) ? rm : 1, (gm === gm) ? gm : 1, (bm === bm) ? bm : 1, (am === am) ? am : 1, 
            // because default value is 0 - this is valid
            +redOffset || 0, +greenOffset || 0, +blueOffset || 0, +alphaOffset || 0);
        }
        return _this;
    }
    ColorTransform.classInitializer = function () {
        this.FROZEN_IDENTITY_COLOR_TRANSFORM = Object.freeze(this.axConstruct([]));
        this.TEMP_COLOR_TRANSFORM = this.axConstruct([]);
    };
    Object.defineProperty(ColorTransform.prototype, "adaptee", {
        // "blueMultiplier", "alphaMultiplier", "redOffset",
        // "greenOffset", "blueOffset", "alphaOffset",
        // "color", "color", "concat", "toString"];
        get: function () {
            return this._adaptee;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "alphaMultiplier", {
        /**
         * A decimal value that is multiplied with the alpha transparency channel
         * value.
         *
         * <p>If you set the alpha transparency value of a display object directly by
         * using the <code>alpha</code> property of the DisplayObject instance, it
         * affects the value of the <code>alphaMultiplier</code> property of that
         * display object's <code>transform.colorTransform</code> property.</p>
         */
        get: function () {
            return this._adaptee._rawData[3];
        },
        set: function (value) {
            this._adaptee._rawData[3] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "alphaOffset", {
        /**
         * A number from -255 to 255 that is added to the alpha transparency channel
         * value after it has been multiplied by the <code>alphaMultiplier</code>
         * value.
         */
        get: function () {
            return this._adaptee._rawData[7];
        },
        set: function (value) {
            this._adaptee._rawData[7] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "blueMultiplier", {
        /**
         * A decimal value that is multiplied with the blue channel value.
         */
        get: function () {
            return this._adaptee._rawData[2];
        },
        set: function (value) {
            this._adaptee._rawData[2] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "blueOffset", {
        /**
         * A number from -255 to 255 that is added to the blue channel value after it
         * has been multiplied by the <code>blueMultiplier</code> value.
         */
        get: function () {
            return this._adaptee._rawData[6];
        },
        set: function (value) {
            this._adaptee._rawData[6] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "greenMultiplier", {
        /**
         * A decimal value that is multiplied with the green channel value.
         */
        get: function () {
            return this._adaptee._rawData[1];
        },
        set: function (value) {
            this._adaptee._rawData[1] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "greenOffset", {
        /**
         * A number from -255 to 255 that is added to the green channel value after
         * it has been multiplied by the <code>greenMultiplier</code> value.
         */
        get: function () {
            return this._adaptee._rawData[5];
        },
        set: function (value) {
            this._adaptee._rawData[5] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "redMultiplier", {
        /**
         * A decimal value that is multiplied with the red channel value.
         */
        get: function () {
            return this._adaptee._rawData[0];
        },
        set: function (value) {
            this._adaptee._rawData[0] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "redOffset", {
        /**
         * A number from -255 to 255 that is added to the red channel value after it
         * has been multiplied by the <code>redMultiplier</code> value.
         */
        get: function () {
            return this._adaptee._rawData[4];
        },
        set: function (value) {
            this._adaptee._rawData[4] = +value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorTransform.prototype, "color", {
        get: function () {
            return this._adaptee.color;
        },
        set: function (value) {
            this._adaptee.color = +value;
        },
        enumerable: false,
        configurable: true
    });
    ColorTransform.prototype.concat = function (second) {
        this._adaptee.concat(second.adaptee);
    };
    ColorTransform.prototype.preMultiply = function (second) {
        this._adaptee._rawData[4] += second.adaptee._rawData[4] * this._adaptee._rawData[0];
        this._adaptee._rawData[5] += second.adaptee._rawData[5] * this._adaptee._rawData[1];
        this._adaptee._rawData[6] += second.adaptee._rawData[6] * this._adaptee._rawData[2];
        this._adaptee._rawData[7] += second.adaptee._rawData[7] * this._adaptee._rawData[3];
        this._adaptee._rawData[0] *= second.adaptee._rawData[0];
        this._adaptee._rawData[1] *= second.adaptee._rawData[1];
        this._adaptee._rawData[2] *= second.adaptee._rawData[2];
        this._adaptee._rawData[3] *= second.adaptee._rawData[3];
    };
    ColorTransform.prototype.copyFrom = function (sourceColorTransform) {
        this._adaptee.copyFrom(sourceColorTransform.adaptee);
    };
    ColorTransform.prototype.setTo = function (redMultiplier, greenMultiplier, blueMultiplier, alphaMultiplier, redOffset, greenOffset, blueOffset, alphaOffset) {
        var rm = +redMultiplier, gm = +greenMultiplier, bm = +blueMultiplier, am = +alphaMultiplier;
        this._adaptee._rawData[0] = (rm === rm) ? rm : 1;
        this._adaptee._rawData[1] = (gm === gm) ? gm : 1;
        this._adaptee._rawData[2] = (bm === bm) ? bm : 1;
        this._adaptee._rawData[3] = (am === am) ? am : 1;
        this._adaptee._rawData[4] = +redOffset || 0;
        this._adaptee._rawData[5] = +greenOffset || 0;
        this._adaptee._rawData[6] = +blueOffset || 0;
        this._adaptee._rawData[7] = +alphaOffset || 0;
    };
    ColorTransform.prototype.clone = function () {
        return new this.sec.flash.geom.ColorTransform(this._adaptee._rawData[0], this._adaptee._rawData[1], this._adaptee._rawData[2], this._adaptee._rawData[3], this._adaptee._rawData[4], this._adaptee._rawData[5], this._adaptee._rawData[6], this._adaptee._rawData[7]);
    };
    ColorTransform.prototype.convertToFixedPoint = function () {
        this._adaptee._rawData[0] = clampS8U8(this._adaptee._rawData[0]);
        this._adaptee._rawData[1] = clampS8U8(this._adaptee._rawData[1]);
        this._adaptee._rawData[2] = clampS8U8(this._adaptee._rawData[2]);
        this._adaptee._rawData[3] = clampS8U8(this._adaptee._rawData[3]);
        this._adaptee._rawData[4] = toS16(this._adaptee._rawData[4]);
        this._adaptee._rawData[5] = toS16(this._adaptee._rawData[5]);
        this._adaptee._rawData[6] = toS16(this._adaptee._rawData[6]);
        this._adaptee._rawData[7] = toS16(this._adaptee._rawData[7]);
        return this;
    };
    ColorTransform.prototype.dispose = function () {
        throw new AbstractMethodError();
    };
    ColorTransform.prototype.equals = function (other) {
        if (this === other)
            return true;
        return this._adaptee._rawData[0] === other._adaptee._rawData[0] &&
            this._adaptee._rawData[1] === other._adaptee._rawData[1] &&
            this._adaptee._rawData[2] === other._adaptee._rawData[2] &&
            this._adaptee._rawData[3] === other._adaptee._rawData[3] &&
            this._adaptee._rawData[4] === other._adaptee._rawData[4] &&
            this._adaptee._rawData[5] === other._adaptee._rawData[5] &&
            this._adaptee._rawData[6] === other._adaptee._rawData[6] &&
            this._adaptee._rawData[7] === other._adaptee._rawData[7];
    };
    ColorTransform.prototype.toString = function () {
        return '(redMultiplier=' + this._adaptee._rawData[0] +
            ', greenMultiplier=' + this._adaptee._rawData[1] +
            ', blueMultiplier=' + this._adaptee._rawData[2] +
            ', alphaMultiplier=' + this._adaptee._rawData[3] +
            ', redOffset=' + this._adaptee._rawData[4] +
            ', greenOffset=' + this._adaptee._rawData[5] +
            ', blueOffset=' + this._adaptee._rawData[6] +
            ', alphaOffset=' + this._adaptee._rawData[7] +
            ')';
    };
    ColorTransform.classSymbols = null; // [];
    ColorTransform.instanceSymbols = null; // ["redMultiplier", "greenMultiplier",
    return ColorTransform;
}(ASObject));
export { ColorTransform };
